import { Box, Spinner, Td, Tr } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import {
  Table as CTable,
  Tbody,
  TableContainer,
} from '@chakra-ui/react';
import { MdOutlineFindInPage } from 'react-icons/md';

import { TableBaseProps, TableFilter, TableGroupRowData } from './types';
import { TableHeader } from './components/table-header';
import { TableColumns } from './components/table-columns';
import { TableRow } from './components/table-row';
import { TablePagination } from './components/table-pagination';
import { TableFilterDialog } from './components/table-filter';
import { Txt } from '../text';
import { isGroupRow } from './utils';
import { TableGroupRow } from './components/table-group-row';

export function TableBase<I extends EntityBase = EntityBase>(props: TableBaseProps<I>) {
  const {
    title,
    columns,
    createMenu,
    onCreateClick,
    onDownload,
    total,
    keyProperty = 'id',
    initialShowFilter,
    data,
    pagination,
    filter,
    sort,
    selectedRow,
    onRowClick,
    onSortChange,
    onFilterChange,
    onPaginationChange,
    loading,
    paper = true,
    disabled,
    additionalToolbar,
    secondaryToolbar,
    footerAdditional,
    renderGroup,
    getRowBackgroundColor,
  } = props;
  const [showFilter, setShowFilter] = useState(initialShowFilter || false);

  const onOpenFilter = useCallback(() => {
    setShowFilter(true);
  }, []);

  const needFilter = useMemo(() => {
    return columns.some((column) => column.options.filter);
  }, [columns]);

  const hasGroups = useMemo(() => {
    return columns.some((column) => !!column.options?.group);
  }, [columns]);

  const onPageChange = useCallback((page: number) => {
    onPaginationChange({
      page,
      pageSize: pagination.pageSize,
    });
  }, [onPaginationChange, pagination?.pageSize]);

  const onPageSizeChange = useCallback((pageSize: number) => {
    onPaginationChange({
      page: 1,
      pageSize,
    });
  }, [onPaginationChange]);

  const onFilterSubmit = useCallback((filter: TableFilter[]) => {
    onFilterChange(filter);
    setShowFilter(false);
  }, [onFilterChange]);

  const visibleColumns = useMemo(() => columns.filter((column) => !column.options?.hidden), [columns]);

  // isolate
  const TableColumnsComp = useMemo(() => <TableColumns<I> 
    sort={sort} columns={visibleColumns} onSortChange={onSortChange} grouped={hasGroups}
  />, [onSortChange, sort, visibleColumns, hasGroups]);

  const TableBody = useMemo(() => {
    let rows: (I | TableGroupRowData<I>)[] = data;
    if (hasGroups) {

      const column = columns.find((column) => !!column.options.group);
      const rowSet = data.reduce<Record<string, I | TableGroupRowData<I>>>((acc, row) => {
        if (typeof row[column.name] !== 'string' && typeof row[column.name] !== 'number')
        {
          throw new Error('Invalid group type (must be primitive (string or number))');
        }
        if (acc[row[column.name]]) {
          if (isGroupRow(acc[row[column.name]])) {
            acc[row[column.name]].items.push(row);
          } else {
            acc[row[column.name]] = {
              _isGroup: true,
              group: row[column.name],
              column,
              items: [
                acc[row[column.name]],
                row,
              ]
            } as unknown as TableGroupRowData<I>;
          } 
        }else {
          acc[row[column.name]] = row;
        }
        
        return acc;
      }, {});
      rows = Object.values(rowSet);
    }
    return <Tbody>
      {data.length === 0 && <Tr>
        <Td colSpan={visibleColumns.length}>
          <Box minH={16} display='flex' gap={4} alignItems='center' justifyContent='center'>
            {!loading && <><MdOutlineFindInPage size={32} color='var(--chakra-colors-gray-400' />
              <Txt color='gray.500'>Пусто</Txt></>}
          </Box>
        </Td>
      </Tr>}
      {rows.map((row: I, rowIndex) => {
        if(isGroupRow<I>(row)) {
          return <TableGroupRow 
            key={`${row.group}_${rowIndex}`} 
            keyProperty={keyProperty}
            rowIndex={rowIndex}
            columns={visibleColumns}
            groupData={row}
            loading={loading}
            selectedRow={selectedRow}
            onRowClick={onRowClick}
            getRowBackgroundColor={getRowBackgroundColor}
          />;
        }
        
        return <TableRow<I>
          key={`${row[keyProperty]}_${rowIndex}`}
          rowIndex={rowIndex}
          columns={visibleColumns}
          rowData={row}
          loading={loading} 
          hasGroups={hasGroups}
          selectedRow={selectedRow}
          onRowClick={onRowClick}
          totalRows={rows.length}
          renderGroup={renderGroup}
          getRowBackgroundColor={getRowBackgroundColor}
        />;
      })}
    </Tbody>;
  }, 
  [columns, data, getRowBackgroundColor, hasGroups, keyProperty, loading, onRowClick, renderGroup, selectedRow, visibleColumns]);

  return <TableContainer 
    backgroundColor='white'
    borderRadius={paper ? 'md' : 'none'}
    boxShadow={paper ? 'md' : 'none'}
    position='relative'
  >
    {(title || needFilter || onDownload || onCreateClick || additionalToolbar || secondaryToolbar || createMenu) && <TableHeader 
      disabled={disabled}
      title={title}
      createMenu={createMenu}
      onFilterClick={needFilter ? onOpenFilter : undefined}
      onDownloadClick={onDownload}
      onCreateClick={onCreateClick}
      additionalToolbar={additionalToolbar}
      secondaryToolbar={secondaryToolbar}
    />}
    <Box overflowX='auto'>
      <CTable variant='simple'>
        {TableColumnsComp }
        {TableBody}
      </CTable>
    </Box>
    {pagination && <TablePagination 
      page={pagination.page || 1}
      pageSize={pagination.pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      footerAdditional={footerAdditional}
    />}
    {loading && <Box 
      position='absolute' backgroundColor='whiteAlpha.500' top={0} left={0} right={0} bottom={0}
      display='flex' alignItems='center' justifyContent='center'
    >
      <Spinner  />
    </Box>}
    <TableFilterDialog<I>
      title={title}
      isOpen={showFilter} 
      onClose={() => setShowFilter(false)}
      columns={columns}
      filters={filter}
      onFilterSubmit={onFilterSubmit}
    /> 
  </TableContainer>;
}